import React, { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Lock, FileText, Check, Store, SlidersHorizontal, X } from 'lucide-react';
import { productSlug, productUrl } from '../pages/ProductPage';

// Price buckets in INR (primary market)
const PRICE_BUCKETS = [
  { label: 'All Prices',          min: 0,     max: Infinity },
  { label: 'Under ₹500',          min: 0,     max: 500 },
  { label: '₹500 – ₹2,000',       min: 500,   max: 2000 },
  { label: '₹2,000 – ₹10,000',    min: 2000,  max: 10000 },
  { label: '₹10,000 – ₹50,000',   min: 10000, max: 50000 },
  { label: 'Over ₹50,000',        min: 50000, max: Infinity },
];

function parsePrice(str) {
  const n = parseFloat(String(str || '').replace(/[^0-9.]/g, ''));
  return isNaN(n) ? null : n;
}

// Sidebar filter pill list
function FilterList({ items, active, onSelect, maxH = 'max-h-44' }) {
  return (
    <div className={`space-y-0.5 ${maxH} overflow-y-auto`}>
      {items.map(item => (
        <button
          key={item}
          onClick={() => onSelect(item)}
          className={`w-full text-left text-xs px-3 py-2 rounded-xl transition-colors ${
            active === item
              ? 'bg-blue-600 text-white font-semibold'
              : 'text-gray-600 hover:bg-gray-50'
          }`}
        >
          {item}
        </button>
      ))}
    </div>
  );
}

/** Image with direct → proxy → gradient fallback chain */
function ProductImage({ src, name, pid }) {
  const [stage, setStage] = useState('direct');
  const [loaded, setLoaded] = useState(false);
  const proxied = src ? `${process.env.REACT_APP_BACKEND_URL}/api/proxy-image?url=${encodeURIComponent(src)}` : null;
  const activeSrc = stage === 'direct' ? src : stage === 'proxy' ? proxied : null;

  if (activeSrc) {
    return (
      <>
        {!loaded && <div className="absolute inset-0 animate-pulse" style={{ background: `linear-gradient(135deg, #dbeafe, #ede9fe)` }} />}
        <img src={activeSrc} alt={name} loading="lazy"
          onLoad={() => setLoaded(true)}
          onError={() => { setLoaded(false); if (stage === 'direct' && proxied) setStage('proxy'); else setStage('fallback'); }}
          className="w-full h-full object-contain transition-opacity duration-300"
          style={{ opacity: loaded ? 1 : 0 }}
        />
      </>
    );
  }
  return (
    <div className="w-full h-full flex items-center justify-center text-xl font-black text-gray-200">
      {(name || '?').charAt(0).toUpperCase()}
    </div>
  );
}

/**
 * Shared cross-brand search results panel.
 * Renders a sidebar (Brand / Category / Price / Pack Size) + product grid.
 * Used by BrandPage, BrandsPage and ProductPage.
 */
export function PartnerSearchResults({
  query,
  results,
  searching,
  onClear,
  backLabel = 'Back',
  isLoggedIn,
  openLabAuth,
  quoteItems = [],
  addToQuote,
  showBrandStoreBadge = false,
  onCardClick,
}) {
  const [brandFilter,    setBrandFilter]    = useState('All');
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [priceBucket,    setPriceBucket]    = useState(PRICE_BUCKETS[0]);
  const [packSizeFilter, setPackSizeFilter] = useState('All');
  const [filtersOpen,    setFiltersOpen]    = useState(false);

  // Reset filters when query or results change
  useEffect(() => {
    setBrandFilter('All');
    setCategoryFilter('All');
    setPriceBucket(PRICE_BUCKETS[0]);
    setPackSizeFilter('All');
  }, [query]);

  // Derive unique filter options (memoised)
  const brands = useMemo(() => {
    const set = new Set(results.map(p => p.brand || '').filter(Boolean));
    return set.size > 1 ? ['All', ...[...set].sort()] : [];
  }, [results]);

  const categories = useMemo(() => {
    const set = new Set(results.map(p => p.category || '').filter(Boolean));
    return set.size > 1 ? ['All', ...[...set].sort()] : [];
  }, [results]);

  const packSizes = useMemo(() => {
    const set = new Set(results.map(p => p.pack_size || '').filter(Boolean));
    return set.size > 1 ? ['All', ...[...set].sort()] : [];
  }, [results]);

  // Apply all filters
  const filtered = useMemo(() => results.filter(p => {
    if (brandFilter    !== 'All' && p.brand     !== brandFilter)    return false;
    if (categoryFilter !== 'All' && p.category  !== categoryFilter) return false;
    if (packSizeFilter !== 'All' && p.pack_size !== packSizeFilter) return false;
    if (priceBucket !== PRICE_BUCKETS[0]) {
      const pr = parsePrice(p.price);
      if (pr !== null && (pr < priceBucket.min || pr >= priceBucket.max)) return false;
    }
    return true;
  }), [results, brandFilter, categoryFilter, packSizeFilter, priceBucket]);

  const hasActive = brandFilter !== 'All' || categoryFilter !== 'All' ||
                    packSizeFilter !== 'All' || priceBucket !== PRICE_BUCKETS[0];

  const clearAll = () => {
    setBrandFilter('All');
    setCategoryFilter('All');
    setPriceBucket(PRICE_BUCKETS[0]);
    setPackSizeFilter('All');
  };

  const hasSidebar = brands.length > 0 || categories.length > 0 || packSizes.length > 0;

  return (
    <div>
      {/* ── Header ── */}
      <div className="flex items-center gap-3 mb-5 flex-wrap">
        <div>
          <h2 className="text-xl font-bold text-gray-900" data-testid="search-results-heading">
            Results for &ldquo;{query}&rdquo;
          </h2>
          <p className="text-sm text-gray-500 mt-0.5">
            {searching
              ? 'Searching…'
              : `${filtered.length} product${filtered.length !== 1 ? 's' : ''} across partner brands`}
          </p>
        </div>
        <button
          onClick={onClear}
          className="ml-auto text-xs text-blue-600 hover:underline border border-blue-200 rounded-full px-3 py-1.5 whitespace-nowrap"
          data-testid="search-clear-btn"
        >
          ← {backLabel}
        </button>
      </div>

      {/* ── Mobile filter toggle ── */}
      {hasSidebar && (
        <button
          onClick={() => setFiltersOpen(o => !o)}
          className="md:hidden flex items-center gap-2 text-sm font-semibold text-blue-600 border border-blue-200 rounded-xl px-4 py-2.5 bg-white mb-4"
          data-testid="mobile-filters-toggle"
        >
          <SlidersHorizontal className="w-4 h-4" />
          {filtersOpen ? 'Hide Filters' : 'Show Filters'}
          {hasActive && <span className="w-2 h-2 rounded-full bg-blue-500" />}
        </button>
      )}

      <div className="flex flex-col md:flex-row gap-5">

        {/* ── Sidebar ── */}
        {hasSidebar && (
          <div
            className={`${filtersOpen ? 'block' : 'hidden'} md:block w-full md:w-52 flex-shrink-0 space-y-4`}
            data-testid="search-filters-sidebar"
          >
            {brands.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-2xl p-4">
                <div className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-3">Brand</div>
                <FilterList items={brands} active={brandFilter} onSelect={setBrandFilter} />
              </div>
            )}

            {categories.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-2xl p-4">
                <div className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-3">Category</div>
                <FilterList items={categories} active={categoryFilter} onSelect={setCategoryFilter} />
              </div>
            )}

            <div className="bg-white border border-gray-200 rounded-2xl p-4">
              <div className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-3">Price Range</div>
              <FilterList items={PRICE_BUCKETS.map(b => b.label)} active={priceBucket.label}
                onSelect={label => setPriceBucket(PRICE_BUCKETS.find(b => b.label === label))} />
            </div>

            {packSizes.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-2xl p-4">
                <div className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-3">Pack Size</div>
                <FilterList items={packSizes} active={packSizeFilter} onSelect={setPackSizeFilter} />
              </div>
            )}

            {hasActive && (
              <button
                onClick={clearAll}
                className="w-full text-xs text-red-500 hover:text-red-700 border border-red-200 rounded-xl px-3 py-2 bg-white flex items-center justify-center gap-1"
                data-testid="clear-filters-btn"
              >
                <X className="w-3.5 h-3.5" /> Clear filters
              </button>
            )}
          </div>
        )}

        {/* ── Main content ── */}
        <div className="flex-1 min-w-0">
          {(searching && results.length === 0) ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {[1,2,3,4,5,6].map(i => (
                <div key={i} className="bg-white border border-gray-200 rounded-2xl p-4 animate-pulse">
                  <div className="h-4 bg-gray-100 rounded w-3/4 mb-2" />
                  <div className="h-3 bg-gray-50 rounded w-1/2 mb-1" />
                  <div className="h-3 bg-gray-50 rounded w-full mb-3" />
                  <div className="h-8 bg-gray-100 rounded-xl w-full" />
                </div>
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-sm font-semibold text-gray-700 mb-1">
                {results.length === 0
                  ? `No products found for "${query}"`
                  : 'No products match the selected filters.'}
              </p>
              {hasActive && (
                <button onClick={clearAll} className="text-xs text-blue-600 hover:underline mt-1">
                  Clear filters
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="search-results-grid">
              {filtered.map(p => {
                const isAdded   = quoteItems.some(q => q.id === p.id);
                const BAD       = ['nan', 'n/a', 'na', '0', ''];
                const rawPrice  = (p.price || '').trim();
                const showPrice = rawPrice && !BAD.includes(rawPrice.toLowerCase());

                return (
                  <Link
                    key={p.id}
                    to={productUrl(p)}
                    rel="nofollow"
                    onClick={onCardClick}
                    data-testid={`search-result-${p.id}`}
                    className="relative bg-white border border-gray-200 rounded-2xl p-4 hover:border-blue-300 hover:shadow-sm transition-all no-underline flex flex-col h-full"
                  >
                    {showBrandStoreBadge && (
                      <div className="absolute top-3 right-3">
                        <div className="flex items-center bg-blue-50 text-blue-600 border border-blue-200 p-1 rounded-full">
                          <Store className="w-3 h-3" />
                        </div>
                      </div>
                    )}
                    <div className={`text-xs font-bold text-blue-600 mb-1 truncate ${showBrandStoreBadge ? 'pr-7' : ''}`}>
                      {p.brand && p.brand !== 'BRAND' ? p.brand : ''}
                    </div>
                    <div className="text-sm font-semibold text-gray-900 leading-snug mb-1 line-clamp-2">
                      {p.name}
                    </div>
                    <div className="flex flex-wrap gap-1 text-xs text-gray-400 mb-1">
                      {p.catalog_number && <span>Cat# {p.catalog_number}</span>}
                      {p.cas_number     && <span>· CAS {p.cas_number}</span>}
                    </div>
                    {p.pack_size && (
                      <div className="text-xs text-gray-400 mb-2">{p.pack_size}</div>
                    )}
                    <div className="mt-auto pt-3">
                      {showPrice && (
                        isLoggedIn
                          ? <div className="text-sm font-bold text-gray-900 mb-2">{rawPrice}</div>
                          : (
                            <button
                              onClick={e => { e.preventDefault(); e.stopPropagation(); openLabAuth('Login to view product prices'); }}
                              className="flex items-center gap-1 text-xs font-semibold text-blue-600 hover:underline mb-2"
                            >
                              <Lock className="w-3 h-3" /> Login to view price
                            </button>
                          )
                      )}
                      <button
                        data-testid={`add-to-quote-${p.id}`}
                        onClick={e => { e.preventDefault(); e.stopPropagation(); if (!isAdded) addToQuote(p); }}
                        className={`w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold transition-colors ${
                          isAdded
                            ? 'bg-green-50 text-green-700 border border-green-200 cursor-default'
                            : 'bg-blue-600 hover:bg-blue-700 text-white'
                        }`}
                      >
                        {isAdded
                          ? <><Check className="w-3.5 h-3.5" /> Added to Quote</>
                          : <><FileText className="w-3.5 h-3.5" /> Request Quote</>
                        }
                      </button>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
